import { useState, useEffect } from 'react'
import { useDialog } from '../../context/DialogContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import {
  Warning,
  MagnifyingGlass,
  Spinner,
  X,
  ArrowClockwise,
  CheckCircle,
  Clock,
  Eye,
  CaretLeft,
  CaretRight,
  Funnel,
} from '@phosphor-icons/react'
import {
  listErrorReports,
  getErrorReport,
  retryError,
  resolveError,
  ErrorReport,
  ErrorReportDetail,
  ErrorReportStats,
  ErrorReportFilters,
} from '../../services/error-reports'

function isRetrySupported(endpoint: string | null): boolean {
  if (!endpoint) return false
  return (
    /^POST\s+\/quotes\/?$/i.test(endpoint) ||
    /^POST\s+\/quotes\/[^/]+\/duplicate/i.test(endpoint)
  )
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getStatusBadge(status: string | null) {
  const s = status || 'pending'
  switch (s) {
    case 'pending':
      return { label: 'Pendente', className: 'bg-amber-100 text-amber-700' }
    case 'resolved':
      return { label: 'Resolvido', className: 'bg-green-100 text-green-700' }
    case 'retried':
      return { label: 'Retentado', className: 'bg-blue-100 text-blue-700' }
    default:
      return { label: s, className: 'bg-gray-100 text-gray-700' }
  }
}

export default function ErrorsPage() {
  const { showSuccess, showError: showDialogError } = useDialog()
  const [isLoading, setIsLoading] = useState(true)
  const [errors, setErrors] = useState<ErrorReport[]>([])
  const [stats, setStats] = useState<ErrorReportStats>({ total: 0, pending: 0, resolved: 0, retried: 0, today: 0 })
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 })
  const [loadError, setLoadError] = useState<string | null>(null)

  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [endpointFilter, setEndpointFilter] = useState<string>('')
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')

  // Detail modal
  const [selectedError, setSelectedError] = useState<ErrorReportDetail | null>(null)
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [isDetailLoading, setIsDetailLoading] = useState(false)

  // Retry/resolve
  const [retryConfirm, setRetryConfirm] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  const loadErrors = async (page = 1) => {
    setIsLoading(true)
    setLoadError(null)
    try {
      const filters: ErrorReportFilters = {
        page,
        limit: 20,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        endpoint: endpointFilter || undefined,
        search: searchTerm || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      }
      const data = await listErrorReports(filters)
      setErrors(data.errors)
      setStats(data.stats)
      setPagination(data.pagination)
    } catch (err: any) {
      console.error('Error loading errors:', err)
      setLoadError(err.message || 'Erro ao carregar erros')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadErrors()
  }, [statusFilter, endpointFilter])

  const handleSearch = () => {
    loadErrors(1)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleOpenDetail = async (errorId: string) => {
    setIsDetailOpen(true)
    setIsDetailLoading(true)
    try {
      const detail = await getErrorReport(errorId)
      setSelectedError(detail)
    } catch (err: any) {
      console.error('Error loading error detail:', err)
      showDialogError('Erro', 'Não foi possível carregar o detalhe do erro')
      setIsDetailOpen(false)
    } finally {
      setIsDetailLoading(false)
    }
  }

  const handleRetry = async (errorId: string) => {
    setActionLoading(errorId)
    try {
      const data = await retryError(errorId)
      const result = data.result as any
      showSuccess(
        'Retry com sucesso',
        result?.txControl
          ? `Cotação ${result.txControl} criada com sucesso`
          : 'Operação retentada com sucesso'
      )
      setRetryConfirm(null)
      setIsDetailOpen(false)
      loadErrors(pagination.page)
    } catch (err: any) {
      console.error('Error retrying:', err)
      showDialogError('Erro no retry', err.message || 'Erro ao retentar operação')
    } finally {
      setActionLoading(null)
    }
  }

  const handleResolve = async (errorId: string) => {
    setActionLoading(`resolve-${errorId}`)
    try {
      await resolveError(errorId)
      showSuccess('Resolvido', 'Erro marcado como resolvido')
      setIsDetailOpen(false)
      loadErrors(pagination.page)
    } catch (err: any) {
      console.error('Error resolving:', err)
      showDialogError('Erro', err.message || 'Erro ao resolver')
    } finally {
      setActionLoading(null)
    }
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Warning size={28} className="text-red-500" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Erros do Sistema</h1>
                <p className="text-sm text-gray-500">
                  Monitore e resolva erros reportados automaticamente
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">Total</div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-amber-600">Pendentes</div>
              <div className="text-2xl font-bold text-amber-600">{stats.pending}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-green-600">Resolvidos</div>
              <div className="text-2xl font-bold text-green-600">{stats.resolved + stats.retried}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm text-blue-600">Hoje</div>
              <div className="text-2xl font-bold text-blue-600">{stats.today}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Funnel size={18} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filtros</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {/* Search */}
            <div className="relative md:col-span-2">
              <MagnifyingGlass
                size={18}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Buscar por mensagem, email, empresa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white"
            >
              <option value="all">Todos os status</option>
              <option value="pending">Pendente</option>
              <option value="resolved">Resolvido</option>
              <option value="retried">Retentado</option>
            </select>

            {/* Endpoint filter */}
            <select
              value={endpointFilter}
              onChange={(e) => setEndpointFilter(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white"
            >
              <option value="">Todos os endpoints</option>
              <option value="POST /quotes">POST /quotes</option>
              <option value="POST /offers">POST /offers</option>
              <option value="POST /quotes/duplicate">POST /quotes/duplicate</option>
            </select>
          </div>

          {/* Date range + search button */}
          <div className="flex items-center gap-3 mt-3">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Data início"
            />
            <span className="text-gray-400 text-sm">até</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
            <Button
              onClick={handleSearch}
              variant="outline"
              className="gap-2 text-sm"
            >
              <MagnifyingGlass size={16} />
              Buscar
            </Button>
          </div>
        </div>

        {/* Error State */}
        {loadError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <Warning size={24} className="text-red-500" />
            <p className="text-red-700">{loadError}</p>
            <button
              onClick={() => loadErrors()}
              className="ml-auto text-red-600 hover:text-red-800 underline text-sm"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {/* Loading */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size={40} className="animate-spin text-red-500" />
          </div>
        ) : errors.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 font-medium">Nenhum erro encontrado</p>
              <p className="text-sm text-gray-400 mt-1">
                {searchTerm || statusFilter !== 'all' || endpointFilter
                  ? 'Tente ajustar os filtros'
                  : 'Nenhum erro reportado no sistema'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Error List */}
            <div className="space-y-3">
              {errors.map((err) => {
                const badge = getStatusBadge(err.status)
                return (
                  <Card key={err.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${badge.className}`}>
                              {badge.label}
                            </span>
                            {err.endpoint && (
                              <code className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                {err.endpoint}
                              </code>
                            )}
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                              <Clock size={12} />
                              {formatDateTime(err.createdAt)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-900 font-medium truncate">
                            {err.errorMessage}
                          </p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            {err.userName && <span>{err.userName}</span>}
                            {err.userEmail && <span>({err.userEmail})</span>}
                            {err.companyName && (
                              <span className="text-gray-400">- {err.companyName}</span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={() => handleOpenDetail(err.id)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Ver detalhes"
                          >
                            <Eye size={20} />
                          </button>
                          {isRetrySupported(err.endpoint) && (err.status === 'pending' || !err.status) && (
                            <button
                              onClick={() => handleRetry(err.id)}
                              disabled={actionLoading === err.id}
                              className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Retry"
                            >
                              {actionLoading === err.id ? (
                                <Spinner size={20} className="animate-spin" />
                              ) : (
                                <ArrowClockwise size={20} />
                              )}
                            </button>
                          )}
                          {(err.status === 'pending' || !err.status) && (
                            <button
                              onClick={() => handleResolve(err.id)}
                              disabled={actionLoading === `resolve-${err.id}`}
                              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Marcar como resolvido"
                            >
                              {actionLoading === `resolve-${err.id}` ? (
                                <Spinner size={20} className="animate-spin" />
                              ) : (
                                <CheckCircle size={20} />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <p className="text-sm text-gray-500">
                  Mostrando {(pagination.page - 1) * pagination.limit + 1}-
                  {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => loadErrors(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="gap-1"
                  >
                    <CaretLeft size={16} />
                    Anterior
                  </Button>
                  <span className="text-sm text-gray-600 px-3">
                    {pagination.page} / {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => loadErrors(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="gap-1"
                  >
                    Próximo
                    <CaretRight size={16} />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Detail Modal */}
      {isDetailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => { setIsDetailOpen(false); setRetryConfirm(null) }} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Detalhe do Erro</h2>
                {selectedError && (
                  <p className="text-sm text-gray-500">
                    {formatDateTime(selectedError.createdAt)}
                  </p>
                )}
              </div>
              <button
                onClick={() => { setIsDetailOpen(false); setRetryConfirm(null) }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[65vh]">
              {isDetailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner size={32} className="animate-spin text-red-500" />
                </div>
              ) : selectedError ? (
                <div className="space-y-5">
                  {/* Status + Endpoint */}
                  <div className="flex items-center gap-3">
                    {(() => {
                      const badge = getStatusBadge(selectedError.status)
                      return (
                        <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${badge.className}`}>
                          {badge.label}
                        </span>
                      )
                    })()}
                    {selectedError.endpoint && (
                      <code className="text-sm bg-gray-100 text-gray-700 px-3 py-1 rounded">
                        {selectedError.endpoint}
                      </code>
                    )}
                  </div>

                  {/* Error message */}
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800 font-medium">{selectedError.errorMessage}</p>
                  </div>

                  {/* User & Company Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-1">Usuário</h4>
                      <p className="text-sm text-gray-900">{selectedError.userName || 'N/A'}</p>
                      <p className="text-xs text-gray-500">{selectedError.userEmail || 'N/A'}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-1">Empresa</h4>
                      <p className="text-sm text-gray-900">{selectedError.companyName || 'N/A'}</p>
                      <p className="text-xs text-gray-500">{selectedError.companyType || 'N/A'}</p>
                    </div>
                  </div>

                  {/* Stack trace */}
                  {selectedError.stackTrace && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Stack Trace</h4>
                      <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap">
                        {selectedError.stackTrace}
                      </pre>
                    </div>
                  )}

                  {/* Request Body */}
                  {selectedError.additionalInfo?.requestBody && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Payload Original (requestBody)</h4>
                      <pre className="bg-gray-100 text-gray-800 p-4 rounded-lg text-xs overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap">
                        {JSON.stringify(selectedError.additionalInfo.requestBody, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Request Params */}
                  {selectedError.additionalInfo?.requestParams && Object.keys(selectedError.additionalInfo.requestParams as object).length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Request Params</h4>
                      <pre className="bg-gray-100 text-gray-800 p-4 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(selectedError.additionalInfo.requestParams, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Retry result */}
                  {selectedError.retryResult && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Resultado do Retry</h4>
                      <pre className="bg-blue-50 text-blue-800 p-4 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(selectedError.retryResult, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Retry Confirmation */}
                  {retryConfirm === selectedError.id && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-amber-800 font-medium mb-2">
                        Confirmar retry?
                      </p>
                      <p className="text-sm text-amber-700 mb-3">
                        {selectedError.endpoint?.includes('/quotes')
                          ? `Criar cotação em nome de ${selectedError.userName || 'usuário'} da ${selectedError.companyName || 'empresa'}?`
                          : `Reexecutar operação em nome de ${selectedError.userName || 'usuário'}?`
                        }
                      </p>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleRetry(selectedError.id)}
                          disabled={actionLoading === selectedError.id}
                          className="gap-2"
                          style={{ backgroundColor: 'rgb(217, 119, 6)' }}
                        >
                          {actionLoading === selectedError.id ? (
                            <>
                              <Spinner size={16} className="animate-spin" />
                              Retentando...
                            </>
                          ) : (
                            <>
                              <ArrowClockwise size={16} />
                              Confirmar Retry
                            </>
                          )}
                        </Button>
                        <Button variant="outline" onClick={() => setRetryConfirm(null)}>
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {/* Footer */}
            {selectedError && (
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
                {(selectedError.status === 'pending' || !selectedError.status) && (
                  <>
                    {isRetrySupported(selectedError.endpoint) && retryConfirm !== selectedError.id && (
                      <Button
                        onClick={() => setRetryConfirm(selectedError.id)}
                        variant="outline"
                        className="gap-2 text-amber-600 border-amber-300 hover:bg-amber-50"
                      >
                        <ArrowClockwise size={18} />
                        Retry
                      </Button>
                    )}
                    <Button
                      onClick={() => handleResolve(selectedError.id)}
                      disabled={actionLoading === `resolve-${selectedError.id}`}
                      variant="outline"
                      className="gap-2 text-green-600 border-green-300 hover:bg-green-50"
                    >
                      {actionLoading === `resolve-${selectedError.id}` ? (
                        <Spinner size={18} className="animate-spin" />
                      ) : (
                        <CheckCircle size={18} />
                      )}
                      Marcar Resolvido
                    </Button>
                  </>
                )}
                <Button variant="outline" onClick={() => { setIsDetailOpen(false); setRetryConfirm(null) }}>
                  Fechar
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  )
}
